/**
 * MA-ARCH-201 Phase 3: Permission-Driven Settings Page
 * 
 * Dynamically renders settings sections based on user's effective permissions.
 * Backend access context is the ONLY source of truth.
 * 
 * CRITICAL: Does NOT use user.role for any visibility logic.
 */

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Facebook, Calendar, Rocket, Globe, Palette, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { MetaAdsSettings } from "@/components/settings/MetaAdsSettings";
import { MetaExecutionCredentials } from "@/components/settings/MetaExecutionCredentials";

function AddCalendarDialog({ onCreated }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [bookingLink, setBookingLink] = useState("");

  const submit = async () => {
    if (!title.trim() || !bookingLink.trim()) {
      toast({ title: "Missing info", description: "Title and Booking Link are required.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await api.post("/calendars", { title: title.trim(), booking_link: bookingLink.trim() });
      toast({ title: "Calendar added" });
      setOpen(false);
      setTitle("");
      setBookingLink("");
      onCreated?.();
    } catch (e) {
      toast({ title: "Could not add calendar", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
          data-testid="settings-add-calendar-button"
        >
          <Plus className="mr-2 h-4 w-4" /> Add Calendar
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-xl"
        data-testid="settings-add-calendar-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="settings-add-calendar-title">Add Calendar</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-200/80" data-testid="settings-add-calendar-title-label">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-white/5 ring-1 ring-white/10"
              data-testid="settings-add-calendar-title-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200/80" data-testid="settings-add-calendar-link-label">Booking link</Label>
            <Input
              value={bookingLink}
              onChange={(e) => setBookingLink(e.target.value)}
              className="bg-white/5 ring-1 ring-white/10"
              placeholder="https://..."
              data-testid="settings-add-calendar-link-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            className="rounded-full bg-white/10 ring-1 ring-white/10"
            onClick={() => setOpen(false)}
            data-testid="settings-add-calendar-cancel"
          >
            Cancel
          </Button>
          <Button
            className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
            onClick={submit}
            disabled={saving}
            data-testid="settings-add-calendar-submit"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * MA-ARCH-201 Phase 3: Settings access denied state
 */
function SettingsAccessDenied() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]" data-testid="settings-access-denied">
      <div className="text-center space-y-4 max-w-md px-6">
        <div className="flex justify-center">
          <div className="rounded-full bg-amber-500/10 p-4">
            <Lock className="h-12 w-12 text-amber-500" />
          </div>
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-slate-100">
            Settings Access Restricted
          </h2>
          <p className="text-sm text-slate-200/70">
            Your account doesn't have access to any settings sections.
            Contact your account administrator if you need access.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { toast } = useToast();
  
  // MA-ARCH-201 Phase 3: Use permissions hook for access control
  const { effectiveSettings, isLoading: permissionsLoading } = usePermissions();
  
  // P0 TENANT ISOLATION: Subscribe to tenant context for automatic refetch
  const { selectedTenantId } = useTenant();
  
  const [settingsTab, setSettingsTab] = useState("");
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);

  // MA-META-OAUTH-034: Handle OAuth callback redirect params
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const oauthResult = searchParams.get("meta_oauth");
    if (oauthResult) {
      // Switch to ghl-integration tab to show result
      setSettingsTab("ghl_integration");
      if (oauthResult === "success") {
        const accounts = searchParams.get("accounts") || "0";
        const business = searchParams.get("business") || "";
        toast({
          title: "Meta Connected",
          description: `Successfully connected ${accounts} ad account(s)${business ? ` for ${business}` : ""}.`,
        });
      } else if (oauthResult === "cancelled") {
        toast({
          title: "Connection Cancelled",
          description: searchParams.get("error") || "Meta authorization was cancelled.",
          variant: "destructive",
        });
      } else if (oauthResult === "error") {
        toast({
          title: "Connection Failed",
          description: searchParams.get("error") || "Failed to connect Meta account.",
          variant: "destructive",
        });
      }
      // Clean URL params
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, toast]);

  // MA-ARCH-201 Phase 3: Set default tab based on available settings
  useEffect(() => {
    if (!permissionsLoading && effectiveSettings.length > 0 && !settingsTab) {
      // Set first available section as default
      setSettingsTab(effectiveSettings[0]);
    }
  }, [effectiveSettings, permissionsLoading, settingsTab]);

  const loadCalendars = async () => {
    setLoading(true);
    try {
      const res = await api.get("/calendars");
      setCalendars(res.data || []);
    } catch (e) {
      // MA-SETTINGS-SECTION-HYDRATION-157: Only show error if Calendars is the active section
      if (settingsTab === "calendar_routes") {
        toast({ title: "Could not load calendars", description: e?.response?.data?.detail || "", variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // MA-SETTINGS-SECTION-HYDRATION-157: Only load calendars when calendar_routes tab is active
    // Previous behavior: loaded on mount if user had access (caused error toast on inactive sections)
    // New behavior: only load when user navigates to Calendars tab
    if (settingsTab === "calendar_routes" && effectiveSettings.includes("calendar_routes")) {
      loadCalendars();
    }
    // P0 TENANT ISOLATION: Refetch when tenant context or active tab changes
  }, [selectedTenantId, settingsTab, effectiveSettings]);

  const onDeleteCalendar = async (id) => {
    try {
      await api.delete(`/calendars/${id}`);
      toast({ title: "Calendar deleted" });
      loadCalendars();
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  // MA-ARCH-201 Phase 3: Show loading state while checking permissions
  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" data-testid="settings-loading">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="text-sm text-slate-200/70">Loading settings...</p>
        </div>
      </div>
    );
  }

  // MA-ARCH-201 Phase 3: Show access denied if no settings sections available
  if (effectiveSettings.length === 0) {
    return <SettingsAccessDenied />;
  }

  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="settings-page">
      <div className="flex items-center justify-between" data-testid="settings-header">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100" data-testid="settings-title">
            Settings
          </h1>
          <div className="mt-1 text-sm text-slate-200/70" data-testid="settings-subtitle">
            Manage your account settings and integrations.
          </div>
          {/* Quick links - only show if user has access */}
          {(effectiveSettings.includes("account_info")) && (
            <div className="flex gap-2 mt-3">
              <Link
                to="/app/settings/brand"
                data-testid="settings-link-brand"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800/60 border border-zinc-700/50 text-zinc-300 text-xs hover:bg-zinc-700/60 hover:text-zinc-100 transition-colors"
              >
                <Palette className="w-3.5 h-3.5" /> Brand
              </Link>
              <Link
                to="/app/settings/domains"
                data-testid="settings-link-domains"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800/60 border border-zinc-700/50 text-zinc-300 text-xs hover:bg-zinc-700/60 hover:text-zinc-100 transition-colors"
              >
                <Globe className="w-3.5 h-3.5" /> Custom Domains
              </Link>
            </div>
          )}
        </div>
      </div>

      <Tabs
        value={settingsTab}
        onValueChange={setSettingsTab}
        className="mt-5 w-full"
        data-testid="settings-tabs"
      >
        <TabsList
          className="inline-flex h-auto w-fit max-w-full flex-wrap justify-start gap-1 bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-400/20"
          data-testid="settings-tabs-list"
        >
          {/* MA-ARCH-201 Phase 3: Dynamically render tabs based on effective permissions */}
          
          {/* Calendar Routes Section */}
          {effectiveSettings.includes("calendar_routes") && (
            <TabsTrigger
              value="calendar_routes"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="settings-tab-calendars"
            >
              <Calendar className="h-4 w-4 mr-1.5" />
              Calendars
            </TabsTrigger>
          )}
          
          {/* GHL Integration Section (Meta Sync) */}
          {effectiveSettings.includes("ghl_integration") && (
            <TabsTrigger
              value="ghl_integration"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="settings-tab-meta-ads"
            >
              <Facebook className="h-4 w-4 mr-1.5" />
              Meta Sync
            </TabsTrigger>
          )}
          
          {/* Additional sections can be added here as they're built */}
          {/* Profile, Team, Billing, Webhooks, Lead Sources, etc. */}
        </TabsList>

        {/* MA-ARCH-201 Phase 3: Dynamically render tab content based on effective permissions */}
        
        {/* Calendar Routes Content */}
        {effectiveSettings.includes("calendar_routes") && (
          <TabsContent value="calendar_routes" className="mt-5" data-testid="settings-tab-content-calendars">
            <Card className="glass" data-testid="settings-calendars-card">
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <CardTitle className="text-slate-100" data-testid="settings-calendars-title">
                    Calendars
                  </CardTitle>
                  <AddCalendarDialog onCreated={loadCalendars} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-hidden rounded-2xl ring-1 ring-white/10" data-testid="settings-calendars-table">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead className="text-slate-200/70" data-testid="settings-calendars-header-title">
                          Title
                        </TableHead>
                        <TableHead className="text-slate-200/70" data-testid="settings-calendars-header-link">
                          Booking link
                        </TableHead>
                        <TableHead className="text-slate-200/70 w-[110px]" data-testid="settings-calendars-header-action">
                          Action
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow className="border-white/10" data-testid="settings-calendars-loading-row">
                          <TableCell colSpan={3} className="py-10 text-center text-sm text-slate-200/70">
                            Loading…
                          </TableCell>
                        </TableRow>
                      ) : calendars.length === 0 ? (
                        <TableRow className="border-white/10" data-testid="settings-calendars-empty-row">
                          <TableCell colSpan={3} className="py-10 text-center text-sm text-slate-200/70">
                            No calendars yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        calendars.map((c) => (
                          <TableRow key={c.id} className="border-white/10" data-testid={`settings-calendar-row-${c.id}`}>
                            <TableCell className="text-slate-100" data-testid={`settings-calendar-title-${c.id}`}>
                              {c.title}
                            </TableCell>
                            <TableCell
                              className="text-xs text-slate-200/70 break-all"
                              data-testid={`settings-calendar-link-${c.id}`}
                            >
                              {c.booking_link}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="secondary"
                                className="h-9 rounded-full bg-white/10 text-rose-300 ring-1 ring-white/10 hover:bg-white/15"
                                onClick={() => onDeleteCalendar(c.id)}
                                data-testid={`settings-calendar-delete-${c.id}`}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* GHL Integration Content (Meta Ads and Execution) */}
        {effectiveSettings.includes("ghl_integration") && (
          <>
            <TabsContent value="ghl_integration" className="mt-5" data-testid="settings-tab-content-meta-ads">
              <div className="space-y-5">
                {/* Meta Ads Integration */}
                <MetaAdsSettings />
                
                {/* Meta Execution Credentials */}
                <MetaExecutionCredentials />
              </div>
            </TabsContent>
          </>
        )}

        {/* Additional settings sections can be added here */}
        {/* Profile, Team, Billing, Webhooks, Lead Sources, Vocabulary, Scripts, API Keys, Platform Config */}
      </Tabs>
    </div>
  );
}
